import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import BugReportIcon from '@mui/icons-material/BugReport';
import { useState } from 'react';
import { useCourtRoles } from '../../../Aemter/hooks';
import { customFetcher } from '../../../hooks/useFetch';

/**
 * Vereinheitlichte „Bearbeitung & Zuständigkeit"-UI für eine Maßnahme bzw.
 * Maßnahme-Vorlage.
 *
 * Modi:
 *  - mode='requirement' (Vorgang): zeigt das aus Gemeinde/Gemarkung +
 *    Gerichtsrolle abgeleitete Gericht (resolvedCourt) sowie eine optionale
 *    manuelle Override-Zuweisung. „Tatsächliche Bearbeitungszeit" ist sichtbar.
 *  - mode='template' (Vorlage): nur Bearbeitungszeit + Gerichtsrolle. Kein
 *    konkretes Gericht — das wird im Vorgang aus der Gemarkung abgeleitet.
 */
export interface IResponsibilityValue {
  initialProcessingTime?: number | null;
  ManualProcessingTime?: boolean;
  actualProcessingTime?: number | null;
  idCourtRole?: number | null;
  CourtRole?: string | null;
  idCourt?: number | null;
  Court?: string | null;
  resolvedCourt?: {
    idCourt?: number | null;
    Court?: string | null;
    fromPhase?: boolean;
    idAdministrativeUnit?: number | null;
    reason?: 'ok' | 'no-court-role' | 'no-admin-unit' | 'no-assignment';
  } | null;
}

interface IProps {
  mode: 'requirement' | 'template';
  value: IResponsibilityValue;
  onChange: (next: IResponsibilityValue) => void;
  onOpenManualCourt?: () => void;
  // Optional: idLegalRequirement, falls vorhanden, blendet einen Diagnose-Button ein,
  // der `/legalrequirement/{id}/diagnose-court` aufruft und die Auflösungs-Kette zeigt.
  idLegalRequirement?: number;
}

export const RequirementResponsibilityCard: React.FC<IProps> = ({
  mode,
  value,
  onChange,
  onOpenManualCourt,
  idLegalRequirement,
}) => {
  const { courtRoles } = useCourtRoles();
  const isTemplate = mode === 'template';
  const hasManualCourt =
    !isTemplate &&
    !!value.idCourt &&
    (!value.resolvedCourt?.idCourt ||
      value.idCourt !== value.resolvedCourt.idCourt);

  // Diagnose-State: zeigt die ganze Auflösungs-Kette aus dem Backend, damit der
  // User bei „kein Gericht zugeordnet" direkt sehen kann, wo die Kette bricht.
  const [diagOpen, setDiagOpen] = useState(false);
  const [diag, setDiag] = useState<unknown>(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagError, setDiagError] = useState<string | null>(null);
  const runDiagnose = async () => {
    if (!idLegalRequirement) return;
    setDiagOpen(true);
    setDiagLoading(true);
    setDiagError(null);
    try {
      const data = await customFetcher({
        endpoint: `/legalrequirement/${idLegalRequirement}/diagnose-court`,
      });
      setDiag(data);
    } catch (e) {
      setDiagError(e instanceof Error ? e.message : 'Diagnose fehlgeschlagen.');
    } finally {
      setDiagLoading(false);
    }
  };

  return (
    <Card variant='outlined'>
      <CardContent>
        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1.5 }}>
          <HourglassBottomIcon color='primary' />
          <Typography variant='subtitle1' fontWeight={600}>
            Bearbeitung &amp; Zuständigkeit
          </Typography>
        </Stack>

        <Grid container spacing={2} alignItems='center'>
          {/* Zeile 1: Bearbeitungszeit + Manuell-Flag */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='Geschätzte Bearbeitungszeit (Tage)'
              type='number'
              fullWidth
              size='small'
              value={value.initialProcessingTime ?? ''}
              onChange={e =>
                onChange({
                  ...value,
                  initialProcessingTime: Number(e.target.value),
                  ManualProcessingTime: true,
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Tooltip title='Manuell gesetzte Zeiten werden nicht durch die automatische Anpassung überschrieben.'>
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    checked={!!value.ManualProcessingTime}
                    onChange={e =>
                      onChange({
                        ...value,
                        ManualProcessingTime: e.target.checked,
                      })
                    }
                  />
                }
                label='Bearbeitungszeit manuell festlegen (keine Auto-Anpassung)'
              />
            </Tooltip>
          </Grid>

          {/* Zeile 2: Tatsächliche Bearbeitungszeit nur im Vorgang */}
          {!isTemplate && (
            <Grid item xs={12} sm={6}>
              <TextField
                label='Tatsächliche Bearbeitungszeit (Tage)'
                type='number'
                fullWidth
                size='small'
                value={value.actualProcessingTime ?? ''}
                onChange={e =>
                  onChange({
                    ...value,
                    actualProcessingTime:
                      e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />
            </Grid>
          )}

          {/* Zeile 3: Gerichtsrolle */}
          <Grid item xs={12} sm={isTemplate ? 12 : 6}>
            <TextField
              label='Notwendige Gerichtsrolle'
              select
              fullWidth
              size='small'
              value={value.idCourtRole ?? ''}
              onChange={e => {
                const v = e.target.value;
                const idCourtRole = v === '' ? null : Number(v);
                const role = courtRoles?.find(
                  r => r.idCourtRole === idCourtRole
                );
                onChange({
                  ...value,
                  idCourtRole,
                  CourtRole: role ? role.CourtRole : null,
                  idCourt: null,
                  Court: null,
                });
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <GavelIcon
                      fontSize='small'
                      sx={{ color: 'text.secondary' }}
                    />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value=''>– keine Gerichtsrolle –</MenuItem>
              {(courtRoles ?? []).map(role => (
                <MenuItem key={role.idCourtRole} value={role.idCourtRole}>
                  {role.CourtRole}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Zeile 4: Anzeige zuständiges Gericht (nur im Vorgang) oder Hinweis (Template) */}
          {isTemplate ? (
            <Grid item xs={12}>
              <Box
                display='flex'
                alignItems='flex-start'
                gap={1}
                sx={{ bgcolor: 'action.hover', p: 1.25, borderRadius: 1 }}
              >
                <InfoOutlinedIcon fontSize='small' color='info' />
                <Typography variant='caption' color='text.secondary'>
                  In Vorlagen wird kein konkretes Gericht festgelegt — es ergibt
                  sich beim Anlegen eines Vorgangs aus der gewählten
                  Gemeinde/Gemarkung und der oben definierten Gerichtsrolle.
                </Typography>
              </Box>
            </Grid>
          ) : (
            <Grid item xs={12}>
              {value.idCourtRole ? (
                value.resolvedCourt?.Court ? (
                  <Stack spacing={0.5}>
                    <Typography variant='caption' color='text.secondary'>
                      Zuständiges Gericht (aus Gemeinde/Gemarkung)
                      {value.resolvedCourt.fromPhase
                        ? ' — Phasen-Override greift'
                        : ''}
                    </Typography>
                    <Stack
                      direction='row'
                      spacing={1}
                      alignItems='center'
                      flexWrap='wrap'
                      useFlexGap
                    >
                      <Chip
                        icon={<GavelIcon />}
                        color='primary'
                        label={value.resolvedCourt.Court}
                        variant='outlined'
                      />
                      {hasManualCourt && (
                        <Chip
                          size='small'
                          color='warning'
                          variant='outlined'
                          label={`Manuell: ${value.Court || ''}`}
                        />
                      )}
                    </Stack>
                  </Stack>
                ) : value.idCourt ? (
                  <Stack spacing={0.5}>
                    <Typography variant='caption' color='text.secondary'>
                      Manuell zugeordnetes Gericht
                    </Typography>
                    <Chip
                      icon={<GavelIcon />}
                      color='warning'
                      variant='outlined'
                      label={`${value.CourtRole || ''} · ${value.Court || ''}`}
                    />
                  </Stack>
                ) : (
                  <Box
                    display='flex'
                    alignItems='flex-start'
                    gap={1}
                    sx={{ bgcolor: 'action.hover', p: 1.25, borderRadius: 1 }}
                  >
                    <InfoOutlinedIcon fontSize='small' color='warning' />
                    <Typography variant='caption' color='text.secondary'>
                      {value.resolvedCourt?.reason === 'no-admin-unit'
                        ? 'Bitte zuerst die Gemeinde/Gemarkung im Vorgang setzen — daraus wird das zuständige Gericht abgeleitet.'
                        : value.resolvedCourt?.reason === 'no-assignment'
                          ? 'Die hinterlegte Gemeinde/Gemarkung hat für diese Rolle kein zugeordnetes Gericht. Bitte in der Ämterverwaltung pflegen oder Gericht manuell festlegen.'
                          : 'Aus der hinterlegten Gemeinde/Gemarkung konnte kein Gericht für diese Rolle abgeleitet werden. Bitte Gemarkung im Vorgang prüfen oder Gericht manuell festlegen.'}
                    </Typography>
                  </Box>
                )
              ) : (
                <Box
                  display='flex'
                  alignItems='flex-start'
                  gap={1}
                  sx={{ bgcolor: 'action.hover', p: 1.25, borderRadius: 1 }}
                >
                  <InfoOutlinedIcon fontSize='small' color='info' />
                  <Typography variant='caption' color='text.secondary'>
                    Wählen Sie eine Gerichtsrolle, damit das zuständige Gericht
                    automatisch aus der Gemeinde/Gemarkung des Vorgangs
                    abgeleitet werden kann.
                  </Typography>
                </Box>
              )}
            </Grid>
          )}

          {/* Zeile 5: Manuelle Override-Aktionen (nur Vorgang) */}
          {!isTemplate && onOpenManualCourt && (
            <Grid item xs={12}>
              <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                <Button
                  size='small'
                  variant='outlined'
                  onClick={onOpenManualCourt}
                >
                  Gericht manuell festlegen
                </Button>
                {hasManualCourt && (
                  <Button
                    size='small'
                    color='warning'
                    onClick={() =>
                      onChange({ ...value, idCourt: null, Court: null })
                    }
                  >
                    Manuelle Zuordnung entfernen
                  </Button>
                )}
                {idLegalRequirement && (
                  <Tooltip title='Diagnose ausführen — zeigt, welche Daten der Server zur Auflösung des zuständigen Gerichts heranzieht.'>
                    <IconButton size='small' onClick={runDiagnose}>
                      <BugReportIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Grid>
          )}
        </Grid>
      </CardContent>

      <Dialog
        open={diagOpen}
        onClose={() => setDiagOpen(false)}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle>Diagnose: Auflösung des zuständigen Gerichts</DialogTitle>
        <DialogContent dividers>
          {diagLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : diagError ? (
            <Alert severity='error'>{diagError}</Alert>
          ) : diag ? (
            <Box
              component='pre'
              sx={{
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                m: 0,
              }}
            >
              {JSON.stringify(diag, null, 2)}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiagOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
